import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CreditCard as SquareIcon,
  CheckCircle as OkIcon,
  LinkOff as DisconnectIcon,
  Link as ConnectIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * Connect the tenant's own Square account (OAuth) and pick the location that
 * receives payments. See SQUARE_INTEGRATION_PLAN.md §4.
 *
 * `result` comes from the OAuth redirect (/dashboard?square=connected|error).
 */
const SquarePaymentsDialog = ({ open, onClose, result }) => {
  const [status, setStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/square/status`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load Square status');
      setStatus(data);

      if (data.connection?.status === 'ACTIVE') {
        const locResponse = await fetch(`${apiBase}/api/admin/square/locations`, { headers: authHeaders() });
        const locData = await locResponse.json();
        if (locResponse.ok) setLocations(locData.locations || []);
      }
    } catch (err) {
      logger.log('[SquarePaymentsDialog] Status fetch failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (!open) return;
    setError(result?.status === 'error' ? `Square connection failed: ${result.reason || 'unknown error'}` : '');
    setSuccess(result?.status === 'connected' ? 'Square account connected.' : '');
    fetchStatus();
  }, [open, result, fetchStatus]);

  const handleConnect = async () => {
    setActionLoading('connect');
    setError('');
    try {
      const response = await fetch(`${apiBase}/api/admin/square/connect-url`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not start the Square connection');
      // Leave the app: Square shows its approval screen, then redirects back to /dashboard
      window.location.assign(data.url);
    } catch (err) {
      setError(err.message);
      setActionLoading('');
    }
  };

  const handleLocationChange = async (locationId) => {
    setActionLoading('location');
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${apiBase}/api/admin/square/location`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ locationId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update the location');
      setStatus((prev) => ({ ...prev, connection: data.connection }));
      setSuccess(`Payments will go to "${data.connection.locationName}".`);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Disconnect Square? Online payments and automatic charges for saved cards will stop until you reconnect.'
      )
    ) {
      return;
    }
    setActionLoading('disconnect');
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${apiBase}/api/admin/square/disconnect`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not disconnect Square');
      setStatus((prev) => ({ ...prev, connection: data.connection }));
      setLocations([]);
      setSuccess('Square disconnected.');
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const connection = status?.connection;
  const isActive = connection?.status === 'ACTIVE';
  const missingConfig = status?.missingConfig || [];

  const statusChip = () => {
    if (!connection || connection.status === 'REVOKED') {
      return <Chip label="Not connected" size="small" variant="outlined" />;
    }
    if (connection.status === 'ERROR') {
      return <Chip label="Needs attention" color="error" size="small" />;
    }
    return <Chip icon={<OkIcon />} label="Connected" color="success" size="small" variant="outlined" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <SquareIcon color="primary" />
          Square Payments
          {status?.environment === 'sandbox' && <Chip label="Sandbox" color="warning" size="small" />}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading && !status ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {status?.environment === 'sandbox' && !isActive && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Sandbox: before clicking Connect, open your seller test account from the Square Developer
                Console (Sandbox test accounts → Open in Square Dashboard) in this same browser. Otherwise
                Square shows a blank page.
              </Alert>
            )}

            {missingConfig.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The server is missing Square settings: {missingConfig.join(', ')}.
              </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">Status:</Typography>
                {statusChip()}
              </Box>

              {connection && connection.status !== 'REVOKED' ? (
                <>
                  <Typography variant="body2">
                    Account: {connection.businessName || connection.merchantId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connected {formatDate(connection.connectedAt)} · token refreshed{' '}
                    {formatDate(connection.lastRefreshedAt)}
                  </Typography>
                  {connection.lastError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {connection.lastError}. Reconnect Square to fix it.
                    </Alert>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Connect your Square account so customers can pay invoices online and saved cards can be
                  charged for recurring cleanings. You'll approve access on Square's website.
                </Typography>
              )}
            </Paper>

            {isActive && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Location that receives payments
                </Typography>
                {!connection.locationId && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Choose a location. Payments are disabled until one is selected.
                  </Alert>
                )}
                <FormControl fullWidth size="small" disabled={actionLoading === 'location'}>
                  <InputLabel id="square-location-label">Location</InputLabel>
                  <Select
                    labelId="square-location-label"
                    label="Location"
                    value={connection.locationId || ''}
                    onChange={(e) => handleLocationChange(e.target.value)}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                        {loc.address?.locality ? ` — ${loc.address.locality}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {isActive && (
          <Button
            color="error"
            startIcon={actionLoading === 'disconnect' ? <CircularProgress size={16} color="inherit" /> : <DisconnectIcon />}
            onClick={handleDisconnect}
            disabled={Boolean(actionLoading)}
          >
            Disconnect
          </Button>
        )}
        <Box flexGrow={1} />
        <Button onClick={onClose}>Close</Button>
        {!isActive && (
          <Button
            variant="contained"
            startIcon={actionLoading === 'connect' ? <CircularProgress size={16} color="inherit" /> : <ConnectIcon />}
            onClick={handleConnect}
            disabled={Boolean(actionLoading) || loading || missingConfig.length > 0}
          >
            {connection ? 'Reconnect Square' : 'Connect Square'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SquarePaymentsDialog;
